import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../App';
import { useResumeEpoch, useTapEpoch } from '../contexts/ResumeContext';
import { toast } from 'sonner';
import { formatEventLocation } from '../utils/locationFormat';
import { getDaysUntilEvent } from '../utils/dateFormatter';
import { VendorAvatar } from '../components/VendorAvatar';
import {
  ArrowLeft, ArrowRight, Calendar, MapPin, Users, DollarSign, Plus, Send, ChevronRight, ChevronDown,
  Sparkles, AlertCircle, Pencil, XCircle, AlertTriangle, MessageSquare,
  CheckCircle2, Circle, Clock, Trash2, Loader2, User, CreditCard,
  ListChecks, Flag, FileText, CheckCircle, Star
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import TimePicker from '../components/ui/TimePicker';
import ComposerSheet from '../components/ui/ComposerSheet';
import CompletionFlow from '../components/CompletionFlow';
import Navigation from '../components/Navigation';
import { getEventTheme } from '../services/eventThemeService';
import { EventDecorations } from '../components/EventDecorations';
import BottomTabBar from '../components/BottomTabBar';
import { CeleyPageLoader } from '../components/CeleyLoader';
import ConversationSwitcher from '../components/conversation/ConversationSwitcher';
import PaymentCheckout from '../components/PaymentCheckout';
import { SendReminderButton } from '../components/SendReminderButton';
import OperationsCenter from '../components/operations/OperationsCenter';
import EventHome from '../components/operations/EventHome';
import { isEventHomeEnabled, shouldRenderEventHome } from '../components/operations/eventHomeFlag';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => n != null ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(n) : null;

const VENDOR_STATUS = {
  pending: { label: 'Awaiting Reply', color: 'bg-gray-100 text-gray-600 border-gray-200' },
  responded: { label: 'Responded', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  proposal_received: { label: 'Proposal Sent', color: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  pending_deposit: { label: 'Pending Payment', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  deposit_paid: { label: 'Booked', color: 'bg-emerald-100 text-emerald-700 border-emerald-200' },
  fully_paid: { label: 'Fully Paid', color: 'bg-emerald-200 text-emerald-800 border-emerald-300' },
  completed_by_vendor: { label: 'Awaiting Confirmation', color: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Completed', color: 'bg-violet-100 text-violet-700 border-violet-200' },
  completion_disputed: { label: 'Under Review', color: 'bg-red-100 text-red-700 border-red-200' },
  declined: { label: 'Declined', color: 'bg-red-100 text-red-700 border-red-200' },
  cancelled: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200' },
  cancelled_by_customer: { label: 'Cancelled', color: 'bg-red-100 text-red-700 border-red-200' },
  cancelled_by_vendor: { label: 'Cancelled by Provider', color: 'bg-red-100 text-red-700 border-red-200' },
};

const CATEGORY_ICONS = { planning: ListChecks, vendor: User, payment: CreditCard, event_day: Flag, budget: DollarSign, custom: Circle };

const EventDetail = () => {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  // Resume / Tap Engine subscriptions — re-pull dashboard + inquiries when
  // iOS reports the app returning to foreground, or when a push tap lands
  // here (even if React Router no-ops because we're already on this route).
  // See ResumeContext architecture (2026-02-28 / 2026-02-29).
  const resumeEpoch = useResumeEpoch();
  const tapEpoch = useTapEpoch();

  const [event, setEvent] = useState(null);
  // Phase 8.6A — authoritative `is_pre_graduation` from the Operations
  // projection. Replaces the prior client-side inference based on
  // `inquiries.length === 0`. The projection layer is the single source
  // of truth for the Event Home routing decision (ADR I18).
  const [isPreGraduation, setIsPreGraduation] = useState(null);
  // B-EXP-6.22 — closure state + brief pointer projected on every
  // customer read. Used only to render the "Concierge planning
  // complete" banner and its "View recommendations" CTA — nothing
  // else on EventDetail branches on this.
  const [caseClosure, setCaseClosure] = useState({ is_case_closed: false });
  const [manualBrief, setManualBrief] = useState({ published: false });
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Dashboard data
  const [vendorTracker, setVendorTracker] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [progress, setProgress] = useState({ vendors_booked: 0, vendors_total: 0, tasks_completed: 0, tasks_total: 0 });
  const [budgetSummary, setBudgetSummary] = useState({});
  const [nextSteps, setNextSteps] = useState([]);

  // Task input
  const [newTask, setNewTask] = useState('');
  const [addingTask, setAddingTask] = useState(false);

  // Modals
  const [conversationInquiryId, setConversationInquiryId] = useState(null);
  const [showPaymentCheckout, setShowPaymentCheckout] = useState(false);
  const [paymentBooking, setPaymentBooking] = useState(null);

  // Edit/Cancel state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [showRequestChangesDialog, setShowRequestChangesDialog] = useState(false);
  const [changesDescription, setChangesDescription] = useState('');
  const [submittingChanges, setSubmittingChanges] = useState(false);

  // Completion flow state
  const [completionBooking, setCompletionBooking] = useState(null);

  // Inline edit state
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState({});
  const [saving, setSaving] = useState(false);

  // In-page tab state (V1.5)
  const [activeTab, setActiveTab] = useState('overview');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = useCallback(async () => {
    if (!user || !eventId) return;
    setLoading(true);
    setError(null);
    try {
      // Fetch both endpoints in parallel
      const [inquiriesRes, dashboardRes, opsRes] = await Promise.all([
        axios.get(`${API}/events/${eventId}/inquiries`, { headers }).catch(() => null),
        axios.get(`${API}/events/${eventId}/dashboard`, { headers }).catch(() => null),
        // Phase 8.6A — fetch the Operations projection so the Event
        // Home routing decision uses the authoritative
        // `is_pre_graduation` boolean. Tolerates 404 (flag off / route
        // hidden) and 403 (vendor / non-owner) — both fall through to
        // the existing EventDetail layout by leaving isPreGraduation
        // null.
        axios.get(`${API}/events/${eventId}/operations`, { headers }).catch(() => null),
      ]);

      if (inquiriesRes?.data) {
        setEvent(inquiriesRes.data.event);
        setInquiries(inquiriesRes.data.inquiries || []);
      }

      if (dashboardRes?.data) {
        // Dashboard data enriches the page
        if (!inquiriesRes?.data) setEvent(dashboardRes.data.event);
        setVendorTracker(dashboardRes.data.vendor_tracker || []);
        setTasks(dashboardRes.data.tasks || []);
        setTimeline(dashboardRes.data.timeline || []);
        setProgress(dashboardRes.data.progress || { vendors_booked: 0, vendors_total: 0, tasks_completed: 0, tasks_total: 0 });
        setBudgetSummary(dashboardRes.data.budget_summary || {});
        setNextSteps(dashboardRes.data.next_steps || []);
      }

      // Phase 8.6A — store the authoritative pre-graduation boolean.
      // Falsy when the projection is unavailable; the routing fork
      // treats null/false identically and falls through to the existing
      // EventDetail layout.
      if (opsRes?.data && typeof opsRes.data.is_pre_graduation === 'boolean') {
        setIsPreGraduation(opsRes.data.is_pre_graduation);
      } else {
        setIsPreGraduation(false);
      }
      // B-EXP-6.22 — capture closure + manual_brief pointer for the
      // completion banner (rendered inside the normal EventDetail shell
      // once the case has graduated on `status == "closed"`). Absence
      // of either block is treated as "no banner needed".
      setCaseClosure(opsRes?.data?.case_closure || { is_case_closed: false });
      setManualBrief(opsRes?.data?.manual_brief || { published: false });

      if (!inquiriesRes?.data && !dashboardRes?.data) {
        setError('Failed to load event details');
        toast.error('Failed to load event details');
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load event details');
      toast.error('Failed to load event details');
    } finally {
      setLoading(false);
    }
  }, [eventId, user]);

  useEffect(() => { if (!authLoading) fetchAll(); }, [authLoading, fetchAll]);
  useEffect(() => { if (!authLoading && !user) navigate('/auth?mode=login&redirect=/events/' + eventId); }, [user, authLoading, navigate, eventId]);
  useEffect(() => {
    if (searchParams.get('refresh') === 'true') {
      fetchAll();
      navigate(`/events/${eventId}`, { replace: true });
    }
  }, [searchParams, eventId, fetchAll, navigate]);

  // Resume/Tap rehydration: refetch when iOS reports foreground or when a
  // push tap lands on this route. Skips the first render so we don't
  // double-fire alongside the mount-effect above. Audit 2026-02-29.
  const firstRehydrateRef = React.useRef(true);
  useEffect(() => {
    if (firstRehydrateRef.current) {
      firstRehydrateRef.current = false;
      return;
    }
    if (!authLoading && user) {
      fetchAll();
    }
  }, [resumeEpoch, tapEpoch, authLoading, user, fetchAll]);

  // ── Stage helpers ──
  const stage = event?.status || 'planning';
  const canEdit = stage === 'draft' || stage === 'planning';
  const canRequestChanges = stage === 'booked' || stage === 'final';

  // Cancellation rule: blocked within 7 days of event (system-level, not UI-only)
  const isWithin7Days = (() => {
    if (!event?.event_date) return false;
    try {
      const evDate = new Date(event.event_date);
      const now = new Date();
      return (evDate - now) / (1000 * 60 * 60 * 24) <= 7;
    } catch { return false; }
  })();
  const canCancel = !['cancelled', 'completed'].includes(stage) && !isWithin7Days;
  const showRequestCancellation = !['cancelled', 'completed'].includes(stage) && isWithin7Days;

  // ── Edit Handlers ──
  const startEditing = () => {
    setEditData({
      event_date: event.event_date?.split('T')[0] || '',
      event_time: event.event_time || '',
      location_city: event.location_city || '',
      location_state: event.location_state || '',
      location_zip: event.location_zip || '',
      guest_count: event.guest_count || '',
      budget: event.budget || '',
    });
    setIsEditing(true);
  };

  const saveEdits = async () => {
    setSaving(true);
    try {
      const payload = {};
      if (editData.event_date) payload.event_date = editData.event_date;
      if (editData.event_time) payload.event_time = editData.event_time;
      if (editData.location_city) payload.location_city = editData.location_city;
      if (editData.location_state) payload.location_state = editData.location_state;
      if (editData.location_zip) payload.location_zip = editData.location_zip;
      if (editData.guest_count) payload.guest_count = parseInt(editData.guest_count) || undefined;
      if (editData.budget) payload.budget = parseFloat(editData.budget) || undefined;

      await axios.patch(`${API}/events/${eventId}`, payload, { headers });
      toast.success('Event updated');
      setIsEditing(false);
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update event');
    } finally {
      setSaving(false);
    }
  };

  // ── Cancel Handler ──
  const handleCancelEvent = async () => {
    setCancelling(true);
    try {
      if (showRequestCancellation) {
        // Request cancellation (within 7 days) — flags for admin review
        await axios.post(`${API}/events/${eventId}/request-cancellation`, { reason: cancelReason || 'Cancellation requested within 7 days of event' }, { headers });
        toast.success('Cancellation request submitted');
      } else {
        const res = await axios.post(`${API}/events/${eventId}/cancel`, { reason: cancelReason || undefined }, { headers });
        // Feb 2026: surface per-booking refund outcomes returned by the
        // backend so the customer sees what happened with their money.
        const d = res?.data || {};
        const total = Number(d.refund_total || 0);
        const refunded = Number(d.refunds_processed || 0);
        const retained = Number(d.deposits_retained || 0);
        const failed = (Array.isArray(d.refund_summary) ? d.refund_summary : [])
          .filter((s) => s && s.success === false).length;

        if (refunded > 0) {
          toast.success(
            `Event cancelled. Refund of $${total.toFixed(2)} processed for ${refunded} booking${refunded === 1 ? '' : 's'}.`,
            { duration: 7000, description: 'Funds will return to your original payment method within 5–10 business days.' }
          );
        } else if (retained > 0) {
          toast.success(
            `Event cancelled. ${retained} deposit${retained === 1 ? '' : 's'} retained by provider${retained === 1 ? '' : 's'} per cancellation policy.`,
            { duration: 7000, description: 'Deposits paid more than 48 hours ago are non-refundable.' }
          );
        } else {
          toast.success('Event cancelled.');
        }

        if (failed > 0) {
          toast.error(
            `${failed} refund${failed === 1 ? '' : 's'} could not be processed automatically.`,
            { duration: 9000, description: 'Our team has been notified and will follow up by email.' }
          );
        }
      }
      setShowCancelDialog(false);
      setCancelReason('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to process cancellation');
    } finally {
      setCancelling(false);
    }
  };

  // ── Request Changes Handler ──
  const handleRequestChanges = async () => {
    if (!changesDescription.trim()) { toast.error('Please describe the changes'); return; }
    setSubmittingChanges(true);
    try {
      await axios.post(`${API}/events/${eventId}/request-changes`, { changes_description: changesDescription }, { headers });
      toast.success('Change request sent to vendor(s)');
      setShowRequestChangesDialog(false);
      setChangesDescription('');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send change request');
    } finally {
      setSubmittingChanges(false);
    }
  };

  // ── Task Handlers ──
  const toggleTask = async (taskId) => {
    setTasks(prev => prev.map(t => t.id === taskId ? { ...t, status: t.status === 'pending' ? 'completed' : 'pending', completed_at: t.status === 'pending' ? new Date().toISOString() : null } : t));
    setProgress(prev => {
      const updatedTasks = tasks.map(t => t.id === taskId ? { ...t, status: t.status === 'pending' ? 'completed' : 'pending' } : t);
      return { ...prev, tasks_completed: updatedTasks.filter(t => t.status === 'completed').length };
    });
    try {
      await axios.patch(`${API}/events/${eventId}/tasks/${taskId}`, {}, { headers });
    } catch {
      toast.error('Failed to update task');
      fetchAll();
    }
  };

  const addTask = async () => {
    if (!newTask.trim()) return;
    setAddingTask(true);
    try {
      await axios.post(`${API}/events/${eventId}/tasks`, { title: newTask.trim() }, { headers });
      setNewTask('');
      fetchAll();
      toast.success('Task added');
    } catch { toast.error('Failed to add task'); }
    finally { setAddingTask(false); }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/events/${eventId}/tasks/${taskId}`, { headers });
      fetchAll();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete task'); }
  };

  // ── Vendor Action Resolution ──
  const getVendorAction = (v) => {
    // Cancellation precedes all other status branches. Customer can re-open
    // the chat to view cancellation details + refund record but no payment
    // or completion CTAs apply.
    if (['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(v.status)) {
      return { label: 'View Chat', icon: MessageSquare, cls: 'bg-slate-50 text-slate-700 hover:bg-slate-100', action: () => setConversationInquiryId(v.inquiry_id) };
    }
    if (v.status === 'declined') return null;
    if (v.status === 'completion_disputed') return { label: 'Under Review', icon: AlertTriangle, cls: 'bg-red-50 text-red-600 cursor-default', action: () => {} };
    if (v.status === 'completed_by_vendor') return {
      label: 'Confirm Completion',
      icon: CheckCircle,
      cls: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200',
      action: () => setCompletionBooking(v),
    };
    if (v.status === 'completed' && !v.has_review) return { label: 'Leave Review', icon: Star, cls: 'bg-violet-50 text-violet-700 hover:bg-violet-100 border border-violet-200', action: () => navigate(`/customer-dashboard?review=${v.inquiry_id}`) };
    if (v.status === 'completed' && v.has_review) return { label: 'Review Submitted', icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-600 cursor-default', action: () => {} };
    if (v.status === 'fully_paid') return { label: 'View Booking', icon: CheckCircle, cls: 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100', action: () => setConversationInquiryId(v.inquiry_id) };
    if (v.status === 'deposit_paid') {
      const isOverdue = v.lifecycle?.final_payment?.state === 'overdue';
      // P0-2: Never remove the payment path from the row when final is overdue.
      // Keep Complete Payment as the primary action so customers can recover.
      // Customers can still tap into chat from the row (action sub-tap) or use
      // the lifecycle banner's secondary "Message Vendor" CTA.
      return { label: 'Complete Payment', icon: CreditCard, cls: isOverdue ? 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200' : 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200', action: () => { setPaymentBooking({ id: v.booking_id, vendor_name: v.vendor_name, agreed_price: v.agreed_price, deposit_amount: v.deposit_amount, final_amount: v.final_amount, deposit_paid: true }); setShowPaymentCheckout(true); } };
    }
    if (v.status === 'pending_deposit' && !v.service_agreement_accepted) return { label: 'Sign Agreement', icon: FileText, cls: 'bg-orange-50 text-orange-700 hover:bg-orange-100', action: () => setConversationInquiryId(v.inquiry_id) };
    if (v.status === 'pending_deposit') {
      const depExpired = v.lifecycle?.deposit?.state === 'expired';
      if (depExpired) return { label: 'Message Vendor', icon: MessageSquare, cls: 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200', action: () => setConversationInquiryId(v.inquiry_id) };
      return { label: 'Pay Deposit', icon: CreditCard, cls: 'bg-amber-50 text-amber-700 hover:bg-amber-100 border border-amber-200', action: () => { setPaymentBooking({ id: v.booking_id, vendor_name: v.vendor_name, agreed_price: v.agreed_price, deposit_amount: v.deposit_amount, final_amount: v.final_amount, deposit_paid: false }); setShowPaymentCheckout(true); } };
    }
    if (v.status === 'proposal_received') return { label: 'Review Proposal', icon: FileText, cls: 'bg-blue-50 text-blue-700 hover:bg-blue-100', action: () => setConversationInquiryId(v.inquiry_id) };
    if (v.status === 'proposal_declined') return { label: 'View Chat', icon: MessageSquare, cls: 'bg-slate-50 text-slate-700 hover:bg-slate-100', action: () => setConversationInquiryId(v.inquiry_id) };
    if (v.status === 'proposal_expired') return { label: 'View Chat', icon: MessageSquare, cls: 'bg-slate-50 text-slate-700 hover:bg-slate-100', action: () => setConversationInquiryId(v.inquiry_id) };
    if (v.status === 'responded') return { label: 'View Chat', icon: MessageSquare, cls: 'bg-slate-50 text-slate-700 hover:bg-slate-100', action: () => setConversationInquiryId(v.inquiry_id) };
    if (v.status === 'pending') return { label: 'View Chat', icon: Send, cls: 'bg-slate-50 text-slate-700 hover:bg-slate-100', action: () => setConversationInquiryId(v.inquiry_id) };
    return null;
  };

  const getActionContext = (v) => {
    if (!v.lifecycle) return null;
    // Cancellation guard: when the booking is cancelled, suppress all
    // deposit/final/cancellation-window countdown subtitles. Lifecycle blocks
    // already report state="cancelled" from the backend, but we also accept
    // the legacy/incomplete payloads (is_cancelled top-level flag, or the
    // inquiry-row status).
    if (v.lifecycle.is_cancelled || ['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor'].includes(v.status)) {
      return null;
    }
    const { deposit: dep, final_payment: fin, cancellation: canc } = v.lifecycle;
    if (dep.state === 'expired') return { text: `Deposit window expired`, cls: 'text-red-600 font-medium' };
    if (dep.state === 'warning') return { text: `Deposit ${dep.countdown?.toLowerCase() || 'due soon'}`, cls: 'text-amber-600 font-medium' };
    if (dep.state === 'active' && dep.countdown) return { text: `Deposit ${dep.countdown?.toLowerCase() || 'due'}`, cls: 'text-blue-600' };
    if (fin.state === 'overdue') return { text: `Final payment ${fin.countdown?.toLowerCase() || 'overdue'}`, cls: 'text-red-600 font-medium' };
    if (fin.state === 'warning') return { text: `Final payment ${fin.countdown?.toLowerCase() || 'due soon'}`, cls: 'text-amber-600 font-medium' };
    if (fin.state === 'active' && fin.countdown) return { text: `Final payment ${fin.countdown?.toLowerCase() || 'due'}`, cls: 'text-blue-600' };
    if (canc.state === 'warning') return { text: canc.countdown, cls: 'text-amber-500' };
    if (canc.state === 'available' && canc.countdown) return { text: `Free cancellation · ${canc.countdown}`, cls: 'text-gray-400' };
    return null;
  };

  // ── Vendor Row Primary Action (context-aware click) ──
  const getVendorRowAction = (v) => {
    // Returns the function to call when the entire row is tapped.
    // Primary action based on state — chat is secondary, not default.
    const act = getVendorAction(v);
    if (act && act.action) return act.action;
    // Fallback: open chat
    return () => setConversationInquiryId(v.inquiry_id);
  };
  if (authLoading || loading) return <CeleyPageLoader text="Loading your event..." />;

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation hideMobileNav={true} />
        <div className="max-w-2xl mx-auto px-4 py-12 text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Event Not Found</h2>
          <p className="text-gray-500 mb-6">{error}</p>
          <Button onClick={() => navigate('/customer-dashboard?tab=events')} variant="outline" data-testid="error-back-btn">Back to Events</Button>
        </div>
      </div>
    );
  }

  if (!event) return null;

  // ── Phase 8.6A — Concierge Event Home routing fork ──
  // The Operations projection's `is_pre_graduation` boolean is the
  // single source of truth for this decision (ADR I18). The frontend
  // gate (`isEventHomeEnabled()`) controls the surface independently of
  // the backend signal, so the feature can be rolled back without
  // disabling the projection layer.
  //
  // When the projection is unavailable (404 with flag off, 403 for
  // non-owners, transient network), `isPreGraduation` is left false and
  // the existing EventDetail layout renders unchanged.
  const isPreGraduationConcierge = shouldRenderEventHome({
    flagEnabled: isEventHomeEnabled(),
    isPreGraduation,
  });
  if (isPreGraduationConcierge) {
    return <EventHome eventId={eventId} />;
  }

  // B-EXP-6.23 — safe date + canonical title. Prevents "Invalid Date"
  // leaking to the header for Concierge events that were created
  // without an event_date, and lets the staff-set title override
  // event_type (matches EventHome / My Events / Case Workspace).
  const _evDate = event.event_date ? new Date(event.event_date) : null;
  const _hasValidDate = !!_evDate && !Number.isNaN(_evDate.getTime());
  const formattedDate = _hasValidDate
    ? _evDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
    : 'Date not set';
  const displayTitle = (event.title && String(event.title).trim())
    || event.event_type
    || 'Event';
  // Closed Concierge case → no active-planning language, no vendor CTAs.
  const isClosedConcierge = event.status === 'closed' && event.planning_style === 'concierge';

  // ── Countdown ──
  const countdown = (() => {
    if (['cancelled', 'completed'].includes(stage)) return null;
    // B-EXP-6.23 — closed Concierge case has graduated; no countdown.
    if (isClosedConcierge) return null;
    if (!_hasValidDate) return null;
    const diff = getDaysUntilEvent(event.event_date);
    if (diff < 0 || !isFinite(diff)) return null; // past event or no date
    if (diff === 0) return { text: 'Today', urgency: 'critical' };
    if (diff === 1) return { text: 'Tomorrow', urgency: 'critical' };
    if (diff <= 3) return { text: `In ${diff} days`, urgency: 'high' };
    if (diff <= 7) return { text: `In ${diff} days`, urgency: 'warm' };
    return { text: `${diff} days to go`, urgency: 'normal' };
  })();

  const getCancelWarning = () => {
    if (stage === 'draft') return null;
    if (stage === 'planning') {
      // Planning events may have pending bookings (pre-deposit)
      const hasPendingBooking = inquiries.some(i => i.booking_id);
      if (hasPendingBooking) return { text: 'This will cancel pending bookings and release vendors.', severity: 'info' };
      return null;
    }
    if (stage === 'booked' && progress.vendors_booked > 0) {
      // Under new definition: booked = deposit paid, so always warn about deposits
      return { text: 'Deposit may be non-refundable. Vendor will be released.', severity: 'warning' };
    }
    return null;
  };

  const handleAddVendors = () => navigate(`/browse?eventId=${eventId}&returnToEvent=true`);

  // Find the inquiry object for conversation switcher
  const conversationInquiry = conversationInquiryId ? inquiries.find(i => i.id === conversationInquiryId) : null;

  // Event identity theme (from centralized service)
  const eventTheme = getEventTheme(event.event_type);

  return (
    <div className="min-h-screen bg-gray-50/50 pb-28" data-testid="event-detail-page">
      <Navigation hideMobileNav={true} />

      {/* ── Sticky Header ── */}
      <div className="bg-white/95 backdrop-blur-sm border-b sticky top-0 z-30" style={{ borderBottomColor: eventTheme.accentColor + '40' }}>
        <div className="max-w-2xl md:max-w-3xl mx-auto px-4 md:px-6 py-3.5 flex items-center gap-3">
          <button
            onClick={() => navigate('/customer-dashboard?tab=events')}
            className="p-1.5 -ml-1.5 hover:bg-gray-100 rounded-full transition-colors"
            data-testid="back-to-events"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-base font-semibold text-gray-900 truncate">{displayTitle}</h1>
            <p className="text-xs text-gray-400">{formattedDate}</p>
          </div>
          {progress.vendors_total > 0 && (
            <Badge variant="outline" className="text-xs shrink-0" data-testid="booked-badge">
              {progress.vendors_booked}/{progress.vendors_total} booked
            </Badge>
          )}
        </div>
      </div>

      {/* ── Content ── */}
      <div className="max-w-2xl md:max-w-3xl mx-auto px-4 md:px-6 pt-6 space-y-5 animate-in fade-in-0 duration-300">

        {/* B-EXP-6.22 — Concierge planning-complete banner.
            Renders only when the customer projection reports
            `case_closure.is_case_closed`. Compact, polished, and
            action-forward: the primary CTA reuses the existing
            manual_brief.share_token to open the canonical brief in
            the same tab. No admin-only fields are surfaced here
            (see _project_case_closure_customer). */}
        {caseClosure?.is_case_closed && (
          <div
            className="p-5 rounded-2xl border border-violet-200 bg-gradient-to-br from-white to-violet-50/40 shadow-sm"
            data-testid="event-concierge-complete-banner"
          >
            <div className="flex items-start gap-3">
              <div
                className="w-9 h-9 rounded-full bg-violet-100 text-violet-600 flex items-center justify-center shrink-0"
                aria-hidden
              >
                <Sparkles className="w-4 h-4" />
              </div>
              <div className="flex-1 min-w-0">
                <p
                  className="text-sm font-semibold text-gray-900"
                  data-testid="event-concierge-complete-title"
                >
                  Concierge planning complete
                </p>
                <p
                  className="text-[13px] text-gray-600 mt-1 leading-relaxed"
                  data-testid="event-concierge-complete-body"
                >
                  Your Concierge recommendations and event information are still available here whenever you need them.
                </p>
                {manualBrief?.published && manualBrief?.share_token ? (
                  <a
                    href={(manualBrief.share_path || `/api/concierge/brief/${encodeURIComponent(manualBrief.share_token)}`) + '?from=events'}
                    className="mt-3 inline-flex items-center gap-1.5 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-medium px-3 py-2 transition-colors"
                    data-testid="event-concierge-complete-cta"
                    data-share-token={manualBrief.share_token}
                  >
                    View recommendations
                    <ArrowRight className="w-3.5 h-3.5" aria-hidden />
                  </a>
                ) : null}
              </div>
            </div>
          </div>
        )}

        {/* Cancelled banner */}
        {stage === 'cancelled' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3" data-testid="cancelled-banner">
            <XCircle className="w-5 h-5 text-red-500 shrink-0" />
            <div>
              <p className="text-sm font-medium text-red-700">This event has been cancelled</p>
              {event.cancel_reason && <p className="text-xs text-red-600 mt-0.5">{event.cancel_reason}</p>}
            </div>
          </div>
        )}

        {/* ── P0-1: Lifecycle Reassurance Banner ──
            Single, top-of-page state-aware reassurance/action banner for the
            customer's highest-priority vendor lifecycle state. Reuses the
            existing vendorTracker priority logic — does NOT introduce a new
            priority system. Suppressed when event is cancelled (the cancel
            banner already speaks for the whole event). */}
        {stage !== 'cancelled' && (
          <LifecycleReassuranceBanner
            vendorTracker={vendorTracker}
            onPay={(v) => {
              setPaymentBooking({
                id: v.booking_id,
                vendor_name: v.vendor_name,
                agreed_price: v.agreed_price,
                deposit_amount: v.deposit_amount,
                final_amount: v.final_amount,
                deposit_paid: !!v.deposit_paid,
              });
              setShowPaymentCheckout(true);
            }}
            onOpenChat={(v) => setConversationInquiryId(v.inquiry_id)}
            onLeaveReview={(v) => navigate(`/customer-dashboard?review=${v.inquiry_id}`)}
            onConfirmCompletion={(v) => setCompletionBooking(v)}
          />
        )}

        {/* ── Section 1: Hero Summary ── */}
        <div
          className="rounded-2xl border border-gray-100/80 p-5 relative overflow-hidden"
          style={{ background: eventTheme.gradient }}
          data-testid="event-overview-card"
        >
          {/* Event-type decorative identity */}
          <EventDecorations decoVariant={eventTheme.decoVariant} decoFlags={eventTheme.decoFlags} eventId={eventId} />
          <div className="flex items-start justify-between gap-3 relative z-10">
            <h2 className="text-lg font-bold text-gray-900">{displayTitle}</h2>
            {countdown && (
              <span className={`text-xs font-medium px-2.5 py-1 rounded-full shrink-0 ${
                countdown.urgency === 'critical' ? 'bg-red-50 text-red-600 ring-1 ring-red-200' :
                countdown.urgency === 'high' ? 'bg-orange-50 text-orange-600 ring-1 ring-orange-200' :
                countdown.urgency === 'warm' ? 'bg-amber-50 text-amber-600 ring-1 ring-amber-100' :
                'bg-gray-50 text-gray-500 ring-1 ring-gray-200'
              }`} data-testid="event-countdown">
                {countdown.text}
              </span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-sm text-gray-500 mt-2 relative z-10">
            <span className="flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5 text-gray-400" />{formattedDate}{event.event_time && ` · ${event.event_time}`}</span>
            {(event.location_city || event.location_zip) && (
              <span className="flex items-center gap-1.5"><MapPin className="w-3.5 h-3.5 text-gray-400" />{formatEventLocation(event) || event.location_zip}</span>
            )}
            {event.guest_count && <span className="flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-gray-400" />{event.guest_count} guests</span>}
            {event.budget && <span className="flex items-center gap-1.5"><DollarSign className="w-3.5 h-3.5 text-gray-400" />{fmt(event.budget)}</span>}
          </div>

          {event.selected_services?.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3 relative z-10">
              {event.selected_services.map(s => (
                <Badge key={s} variant="outline" className="text-xs font-normal">{s}</Badge>
              ))}
            </div>
          )}

          {/* Inline progress summary */}
          {stage !== 'cancelled' && (progress.vendors_total > 0 || progress.tasks_total > 0) && (
            <div className="flex items-center gap-3 mt-3 text-xs text-gray-500 relative z-10" data-testid="hero-progress-summary">
              {progress.vendors_total > 0 && (
                <span className="flex items-center gap-1">
                  <User className="w-3 h-3 text-gray-400" />
                  <span className="font-medium text-gray-700">{progress.vendors_booked}/{progress.vendors_total}</span> booked
                </span>
              )}
              {progress.tasks_total > 0 && (
                <span className="flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3 text-gray-400" />
                  <span className="font-medium text-gray-700">{progress.tasks_completed}/{progress.tasks_total}</span> tasks
                </span>
              )}
            </div>
          )}

          {/* Event Actions */}
          {stage !== 'cancelled' && stage !== 'completed' && (
            <div className="mt-4 pt-3 border-t border-gray-50 flex flex-wrap gap-2" data-testid="event-actions">
              {canEdit && (
                <Button size="sm" variant="outline" className="text-xs h-8 gap-1.5" onClick={startEditing} data-testid="edit-event-btn">
                  <Pencil className="w-3.5 h-3.5" /> Edit Event
                </Button>
              )}
              {canRequestChanges && (
                <Button size="sm" variant="outline" className="text-xs h-8 gap-1.5" onClick={() => setShowRequestChangesDialog(true)} data-testid="request-changes-btn">
                  <MessageSquare className="w-3.5 h-3.5" /> Request Changes
                </Button>
              )}
              {stage === 'final' && (
                <p className="text-xs text-gray-400 italic flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" /> Editing disabled — event is within 7 days
                </p>
              )}
              {canCancel && (
                <Button size="sm" variant="ghost" className="text-xs h-8 gap-1.5 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setShowCancelDialog(true)} data-testid="cancel-event-btn">
                  <XCircle className="w-3.5 h-3.5" /> Cancel Event
                </Button>
              )}
              {showRequestCancellation && (
                <div className="w-full mt-1">
                  <p className="text-[11px] text-amber-600 flex items-center gap-1 mb-1">
                    <AlertCircle className="w-3 h-3 shrink-0" /> Event is within 7 days — direct cancellation is disabled
                  </p>
                  <Button size="sm" variant="outline" className="text-xs h-8 gap-1.5 border-amber-200 text-amber-700 hover:bg-amber-50" onClick={() => setShowCancelDialog(true)} data-testid="request-cancellation-btn">
                    Request Cancellation
                  </Button>
                </div>
              )}
            </div>
          )}

          {stage === 'planning' && isEditing && (
            <div className="mt-2 p-2 bg-amber-50 border border-amber-100 rounded-lg">
              <p className="text-xs text-amber-700 flex items-center gap-1.5">
                <AlertTriangle className="w-3 h-3 shrink-0" /> Changes may affect vendor availability or pricing
              </p>
            </div>
          )}
        </div>

        {/* ── Inline Edit Panel ── */}
        {isEditing && (
          <div className="bg-white rounded-2xl border border-emerald-200 p-5 space-y-4" data-testid="edit-panel">
            <h3 className="text-sm font-semibold text-gray-900">Edit Event Details</h3>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs text-gray-500">Date</Label><Input type="date" value={editData.event_date} onChange={(e) => setEditData({...editData, event_date: e.target.value})} className="mt-1 text-sm" /></div>
              <div><Label className="text-xs text-gray-500">Time</Label><TimePicker value={editData.event_time} onChange={(e) => setEditData({...editData, event_time: e.target.value})} className="mt-1 text-sm" /></div>
              <div><Label className="text-xs text-gray-500">City</Label><Input value={editData.location_city} onChange={(e) => setEditData({...editData, location_city: e.target.value})} className="mt-1 text-sm" placeholder="City" /></div>
              <div><Label className="text-xs text-gray-500">State</Label><Input value={editData.location_state} onChange={(e) => setEditData({...editData, location_state: e.target.value.toUpperCase()})} className="mt-1 text-sm" placeholder="ST" maxLength={2} /></div>
              <div><Label className="text-xs text-gray-500">ZIP</Label><Input value={editData.location_zip} onChange={(e) => setEditData({...editData, location_zip: e.target.value})} className="mt-1 text-sm" placeholder="ZIP" /></div>
              <div><Label className="text-xs text-gray-500">Guests</Label><Input type="number" value={editData.guest_count} onChange={(e) => setEditData({...editData, guest_count: e.target.value})} className="mt-1 text-sm" /></div>
              <div><Label className="text-xs text-gray-500">Budget</Label><Input type="number" value={editData.budget} onChange={(e) => setEditData({...editData, budget: e.target.value})} className="mt-1 text-sm" placeholder="$" /></div>
            </div>
            <div className="flex gap-2 pt-1">
              <Button size="sm" onClick={saveEdits} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-xs h-8" data-testid="save-edits-btn">{saving ? 'Saving...' : 'Save Changes'}</Button>
              <Button size="sm" variant="outline" onClick={() => setIsEditing(false)} className="text-xs h-8">Cancel</Button>
            </div>
          </div>
        )}

        {/* ── Section 2: Progress (folded into hero as inline summary) ── */}
        {/* Removed — progress now shown in hero via inline badges */}

        {/* ── In-Page Tabs (V1.5) ── */}
        <div
          className="bg-white rounded-2xl border border-gray-100/80 p-1 flex gap-1 text-sm sticky top-0 z-10"
          data-testid="event-detail-tabs"
        >
          {[
            { key: 'overview',  label: 'Vendors'   },
            { key: 'checklist', label: 'Checklist' },
            { key: 'budget',    label: 'Budget'    },
            { key: 'timeline',  label: 'Timeline'  },
          ].map(t => (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              className={`flex-1 py-2 rounded-xl transition-colors font-medium ${
                activeTab === t.key
                  ? 'bg-emerald-600 text-white'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              data-testid={`event-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab: Overview ── */}
        {activeTab === 'overview' && (
          <VendorActionCenter
            vendorTracker={vendorTracker}
            inquiries={inquiries}
            stage={stage}
            event={event}
            getVendorAction={getVendorAction}
            getActionContext={getActionContext}
            getVendorRowAction={getVendorRowAction}
            handleAddVendors={handleAddVendors}
            setConversationInquiryId={setConversationInquiryId}
            setCompletionBooking={setCompletionBooking}
            setPaymentBooking={setPaymentBooking}
            setShowPaymentCheckout={setShowPaymentCheckout}
            navigate={navigate}
          />
        )}

        {/* ── Tab: Checklist ── */}
        {activeTab === 'checklist' && tasks.length > 0 && !['cancelled'].includes(stage) && (
          <Card data-testid="planning-checklist">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center justify-between">
                <span className="flex items-center gap-2"><ListChecks className="h-4 w-4 text-amber-600" />Planning Checklist</span>
                <span className="text-xs font-normal text-gray-400">{progress.tasks_completed}/{progress.tasks_total} done</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {stage !== 'completed' && (
                <div
                  className="bg-white border border-gray-200 rounded-xl mb-3 flex items-stretch overflow-hidden focus-within:border-emerald-400 focus-within:ring-1 focus-within:ring-emerald-200 transition-colors"
                  data-testid="checklist-create-zone"
                >
                  <Input
                    placeholder="What needs to get done?"
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && addTask()}
                    className="flex-1 h-10 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none text-sm px-3"
                    data-testid="new-task-input"
                  />
                  <div className="my-2 border-l border-gray-200" />
                  <button
                    type="button"
                    onClick={addTask}
                    disabled={addingTask || !newTask.trim()}
                    aria-label="Add task"
                    data-testid="add-task-btn"
                    className="shrink-0 w-10 flex items-center justify-center bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white active:bg-emerald-600 disabled:opacity-40 disabled:hover:bg-emerald-50 disabled:hover:text-emerald-600 disabled:cursor-not-allowed transition-colors"
                  >
                    {addingTask ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </button>
                </div>
              )}
              <div className="space-y-0.5">
                {tasks.map((t) => {
                  const Icon = CATEGORY_ICONS[t.category] || Circle;
                  const done = t.status === 'completed';
                  const autoCompleted = done && t.completed_by === 'system';
                  const isLocked = stage === 'completed';
                  return (
                    <div
                      key={t.id}
                      role="button"
                      tabIndex={isLocked ? -1 : 0}
                      onClick={() => !isLocked && toggleTask(t.id)}
                      onKeyDown={(e) => { if (!isLocked && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); toggleTask(t.id); } }}
                      className={`flex items-center gap-2 py-2 px-2 -mx-2 rounded-lg select-none transition-colors ${isLocked ? 'cursor-default' : 'cursor-pointer hover:bg-gray-50 active:bg-gray-100'}`}
                      data-testid={`task-${t.id}`}
                    >
                      <span className={`shrink-0 h-5 w-5 rounded-full border-2 flex items-center justify-center transition-all ${done ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 group-hover:border-emerald-400'} ${!isLocked ? 'active:scale-90' : ''}`} data-testid={`toggle-task-${t.id}`}>
                        {done && <CheckCircle2 className="h-3.5 w-3.5 text-white" />}
                      </span>
                      <Icon className={`h-3.5 w-3.5 shrink-0 ${done ? 'text-gray-300' : 'text-gray-400'}`} />
                      <span className={`text-sm flex-1 ${done ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                        {t.title}
                        {autoCompleted && <span className="ml-1.5 text-[10px] text-emerald-500 font-medium align-middle" data-testid={`auto-tag-${t.id}`}>auto</span>}
                      </span>
                      {!t.auto_generated && !isLocked && (
                        <button
                          onClick={(e) => { e.stopPropagation(); deleteTask(t.id); }}
                          className="shrink-0 p-1.5 -m-1.5 text-gray-400 hover:text-red-600 active:text-red-700 transition-colors"
                          aria-label="Delete task"
                          data-testid={`delete-task-${t.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {activeTab === 'checklist' && tasks.length === 0 && !['cancelled'].includes(stage) && (
          <Card data-testid="planning-checklist-empty">
            <CardHeader className="pb-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2"><ListChecks className="h-4 w-4 text-amber-600" />Planning Checklist</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-gray-400 mb-3" data-testid="checklist-empty-msg">No tasks yet. Add the first one below.</p>
              <div className="bg-white border border-gray-200 rounded-xl flex items-stretch overflow-hidden focus-within:border-emerald-400 focus-within:ring-1 focus-within:ring-emerald-200 transition-colors">
                <Input
                  placeholder="What needs to get done?"
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && addTask()}
                  className="flex-1 h-10 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:outline-none text-sm px-3"
                  data-testid="new-task-input"
                />
                <div className="my-2 border-l border-gray-200" />
                <button
                  type="button"
                  onClick={addTask}
                  disabled={addingTask || !newTask.trim()}
                  aria-label="Add task"
                  data-testid="add-task-btn"
                  className="shrink-0 w-10 flex items-center justify-center bg-emerald-50 text-emerald-600 hover:bg-emerald-500 hover:text-white active:bg-emerald-600 disabled:opacity-40 disabled:hover:bg-emerald-50 disabled:hover:text-emerald-600 disabled:cursor-not-allowed transition-colors"
                >
                  {addingTask ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                </button>
              </div>
            </CardContent>
          </Card>
        )}
        {activeTab === 'checklist' && ['cancelled'].includes(stage) && (
          <div className="bg-white rounded-2xl border border-gray-100/80 p-6 text-center text-sm text-gray-400" data-testid="checklist-empty">
            Checklist hidden for cancelled events.
          </div>
        )}

        {/* ── Tab: Budget ── */}
        {activeTab === 'budget' && (
          <BudgetSection
            budget={budgetSummary}
            vendorTracker={vendorTracker}
            selectedVendors={event?.selected_vendors || []}
            defaultExpanded={true}
          />
        )}

        {/* ── Tab: Timeline ── */}
        {activeTab === 'timeline' && timeline.length > 0 && stage !== 'cancelled' && (
          <Card data-testid="event-timeline">
            <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-2"><Clock className="h-4 w-4 text-blue-600" />Event Timeline</CardTitle></CardHeader>
            <CardContent>
              <div className="relative pl-6">
                {timeline.map((m, i) => {
                  const isLast = i === timeline.length - 1;
                  const completed = m.status === 'completed';
                  const upcoming = m.status === 'upcoming';
                  return (
                    <div key={i} className="relative pb-5" data-testid={`milestone-${i}`}>
                      {!isLast && <div className={`absolute left-[-17px] top-5 w-0.5 h-full ${completed ? 'bg-emerald-300' : 'bg-gray-200'}`} />}
                      <div className={`absolute left-[-22px] top-1 h-3 w-3 rounded-full border-2 ${completed ? 'bg-emerald-500 border-emerald-500' : upcoming ? 'bg-blue-400 border-blue-400' : 'bg-white border-gray-300'}`} />
                      <div>
                        <p className={`text-sm font-medium ${completed ? 'text-gray-900' : 'text-gray-400'}`}>{m.label}</p>
                        {m.timestamp && <p className="text-xs text-gray-400 mt-0.5">{formatTimestamp(m.timestamp)}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}
        {activeTab === 'timeline' && (timeline.length === 0 || stage === 'cancelled') && (
          <div className="bg-white rounded-2xl border border-gray-100/80 p-6 text-center text-sm text-gray-400" data-testid="timeline-empty">
            {stage === 'cancelled' ? 'Timeline hidden for cancelled events.' : 'No timeline yet.'}
          </div>
        )}

        {/* ── Phase 8e: Event Operations Center ──
            Calm, additive customer-facing summary mounted BENEATH the
            existing Event Command Center. Gated client-side by
            `REACT_APP_ENABLE_EVENT_OPERATIONS_CENTER` and server-side by
            `ENABLE_EVENT_OPERATIONS_CENTER` (the route 404s when off,
            and the component silently renders nothing). Suppressed for
            cancelled events. */}
        {stage !== 'cancelled' && (
          <OperationsCenter eventId={eventId} refreshSignal={resumeEpoch} />
        )}
      </div>

      {/* ── Cancel Event Dialog ── */}
      <Dialog open={showCancelDialog} onOpenChange={(open) => { if (!open) { setShowCancelDialog(false); setCancelReason(''); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <XCircle className="h-5 w-5" />{showRequestCancellation ? 'Request Cancellation' : 'Cancel Event'}
            </DialogTitle>
            <DialogDescription>
              {showRequestCancellation
                ? 'This event is within 7 days. Cancellation requires admin review to protect vendor commitments and escrow funds.'
                : (stage === 'draft' || stage === 'planning') ? 'This will delete your event plan.'
                : stage === 'booked' ? 'This will cancel bookings and release vendors.'
                : 'This will cancel all bookings for this event.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {getCancelWarning() && (
              <div className={`p-3 rounded-lg border text-sm ${getCancelWarning().severity === 'warning' ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-blue-50 border-blue-200 text-blue-700'}`}>
                <div className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /><span>{getCancelWarning().text}</span></div>
              </div>
            )}
            <div>
              <Label className="text-sm text-gray-700">Reason for cancellation {showRequestCancellation ? '(required)' : '(optional)'}</Label>
              <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="e.g., Change of plans, date conflict..." className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm h-20 resize-none mt-1" data-testid="cancel-reason-input" />
            </div>
            <div className="flex gap-3">
              <Button variant="outline" onClick={() => setShowCancelDialog(false)} className="flex-1" disabled={cancelling}>Keep Event</Button>
              <Button onClick={handleCancelEvent} className={`flex-1 text-white ${showRequestCancellation ? 'bg-amber-600 hover:bg-amber-700' : 'bg-red-600 hover:bg-red-700'}`} disabled={cancelling || (showRequestCancellation && !cancelReason.trim())} data-testid="confirm-cancel-btn">
                {cancelling ? 'Submitting...' : showRequestCancellation ? 'Submit Request' : 'Yes, Cancel Event'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Request Changes Composer ── */}
      <ComposerSheet
        open={showRequestChangesDialog}
        onClose={() => { setShowRequestChangesDialog(false); setChangesDescription(''); }}
        title="Request Changes"
        subtitle="Describe the changes you need. Your vendor(s) will be notified."
        icon={<MessageSquare className="h-5 w-5 text-emerald-600" />}
        testId="request-changes-composer"
        maxWidth="sm:max-w-md"
        footer={
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowRequestChangesDialog(false)} className="flex-1" disabled={submittingChanges}>Cancel</Button>
            <Button onClick={handleRequestChanges} className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white" disabled={submittingChanges || !changesDescription.trim()} data-testid="submit-changes-btn">
              {submittingChanges ? 'Sending...' : 'Send to Vendor(s)'}
            </Button>
          </div>
        }
      >
        <div>
          <label className="text-sm font-medium text-gray-700 block mb-1.5">What would you like to change?</label>
          <textarea
            value={changesDescription}
            onChange={(e) => setChangesDescription(e.target.value)}
            placeholder="e.g., We need to change the date to March 15, can we also add 20 more guests..."
            className="w-full border border-gray-200 rounded-xl px-3.5 py-3 text-sm min-h-[120px] resize-none focus:ring-2 focus:ring-emerald-200 focus:border-emerald-400 outline-none transition-all"
            autoFocus
            data-testid="changes-description"
          />
          <p className="text-xs text-gray-400 mt-1.5">This message will be sent to all booked vendors for this event.</p>
        </div>
      </ComposerSheet>

      {/* Conversation Thread */}
      {conversationInquiry && (
        <ConversationSwitcher
          inquiryId={conversationInquiry.id}
          isOpen={!!conversationInquiry}
          onClose={() => { setConversationInquiryId(null); fetchAll(); }}
          userRole="customer"
          __diagSelectedInquiry={conversationInquiry}
          onProposalAccepted={(booking) => {
            // Accept Proposal → continue directly to Pay Deposit/Checkout.
            // Mirrors CustomerDashboard.handleProposalAccepted + the onPayNow
            // path below: closes the chat modal, hydrates the payment sheet
            // from the new booking, opens the checkout. fetchAll() still runs
            // so the Home/Event Pay Deposit/Pay Final cards keep working as
            // a fallback if the user closes the sheet.
            setConversationInquiryId(null);
            if (booking?.id) {
              const v = vendorTracker.find(vt => vt.booking_id === booking.id);
              setPaymentBooking(v ? {
                id: v.booking_id,
                vendor_name: v.vendor_name,
                agreed_price: v.agreed_price,
                deposit_amount: v.deposit_amount,
                final_amount: v.final_amount,
                deposit_paid: false,
              } : {
                id: booking.id,
                vendor_name: booking.vendor_name,
                agreed_price: booking.agreed_price,
                deposit_amount: booking.deposit_amount,
                final_amount: booking.final_amount,
                deposit_paid: false,
              });
              setShowPaymentCheckout(true);
            }
            fetchAll();
          }}
          inquiryStatus={conversationInquiry.status}
          onPayNow={(bookingId) => {
            setConversationInquiryId(null);
            const v = vendorTracker.find(vt => vt.booking_id === bookingId);
            if (v) setPaymentBooking({ id: v.booking_id, vendor_name: v.vendor_name, agreed_price: v.agreed_price, deposit_amount: v.deposit_amount, final_amount: v.final_amount, deposit_paid: v.deposit_paid });
            else setPaymentBooking({ id: bookingId });
            setShowPaymentCheckout(true);
          }}
          onLeaveReview={(inquiryId) => {
            setConversationInquiryId(null);
            navigate(`/customer-dashboard?review=${inquiryId}`);
          }}
        />
      )}

      {/* Payment Checkout Modal */}
      <PaymentCheckout
        bookingId={paymentBooking?.id}
        isOpen={showPaymentCheckout && !!paymentBooking}
        onClose={() => { setShowPaymentCheckout(false); setPaymentBooking(null); fetchAll(); }}
        onSuccess={() => { setShowPaymentCheckout(false); setPaymentBooking(null); fetchAll(); toast.success('Payment completed!'); }}
        context={paymentBooking ? { vendorName: paymentBooking.vendor_name, eventName: event?.event_type, eventDate: event?.event_date } : null}
      />

      {/* Completion Flow */}
      <CompletionFlow
        open={!!completionBooking}
        onClose={() => setCompletionBooking(null)}
        booking={completionBooking}
        vendorName={completionBooking?.vendor_name}
        eventType={event?.event_type}
        onCompleted={fetchAll}
      />

      {user && <BottomTabBar />}
    </div>
  );
};

// ── Progress Section ──
// ── Budget Section ──
const BUDGET_CANCELLED_STATUSES = new Set(['cancelled', 'cancelled_by_customer', 'cancelled_by_vendor']);
const BUDGET_STATUS_LABEL = {
  pending_deposit: 'Deposit pending',
  deposit_paid: 'Deposit paid',
  fully_paid: 'Paid',
  completed: 'Completed',
  completed_by_vendor: 'Awaiting confirmation',
  completion_disputed: 'Disputed',
};

// ── P0-1: Lifecycle Reassurance Banner ────────────────────────────────────
// Single, top-of-page state-aware action+reassurance banner. Picks the
// highest-priority vendor lifecycle state from vendorTracker and renders
// one banner. Mirrors getVendorPriority semantics from VendorActionCenter
// (intentionally inlined here so the banner is independent of that
// component and can render before the Vendors tab is mounted).
function LifecycleReassuranceBanner({ vendorTracker, onPay, onOpenChat, onLeaveReview, onConfirmCompletion }) {
  if (!Array.isArray(vendorTracker) || vendorTracker.length === 0) return null;

  // Priority — lower = more urgent. Only states worth surfacing as a banner.
  // Passive states (pending, declined, etc.) intentionally return null.
  const scoreOf = (v) => {
    if (v.status === 'declined' || v.status === 'completion_disputed') return null;
    if (v.status === 'deposit_paid' && v.lifecycle?.final_payment?.state === 'overdue') return 1; // overdue final
    if (v.lifecycle?.deposit?.state === 'expired') return 2; // deposit expired
    if (v.status === 'completed_by_vendor') return 3; // confirm completion
    if (v.status === 'deposit_paid' && v.lifecycle?.final_payment?.state === 'warning') return 4; // final due soon
    if (v.status === 'pending_deposit' && v.lifecycle?.deposit?.state === 'warning') return 5; // deposit due soon
    if (v.status === 'proposal_received') return 6;
    if (v.status === 'pending_deposit') return 7; // awaiting deposit
    if (v.status === 'deposit_paid') return 8; // booked, reassurance only
    if (v.status === 'completed' && !v.has_review) return 9;
    return null;
  };

  // Pick highest-priority vendor
  let topVendor = null;
  let topScore = Infinity;
  for (const v of vendorTracker) {
    const s = scoreOf(v);
    if (s != null && s < topScore) { topScore = s; topVendor = v; }
  }
  if (!topVendor) return null;

  const fmt = (n) => `$${(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
  const countdown = topVendor.lifecycle?.final_payment?.countdown
    || topVendor.lifecycle?.deposit?.countdown
    || '';
  const v = topVendor;

  // State templates — single banner per state.
  let banner;
  switch (topScore) {
    case 1: // Overdue final payment
      banner = {
        tone: 'red',
        title: `Final payment overdue for ${v.vendor_name}`,
        body: v.final_amount ? `${fmt(v.final_amount)} due now` : 'Payment is overdue',
        primary: { label: 'Complete Payment', onClick: () => onPay(v), testId: 'lifecycle-banner-pay' },
        secondary: { label: 'Message Vendor', onClick: () => onOpenChat(v), testId: 'lifecycle-banner-message' },
      };
      break;
    case 2: // Deposit window expired
      banner = {
        tone: 'red',
        title: `Deposit window expired for ${v.vendor_name}`,
        body: 'Reach out to your vendor to resolve.',
        primary: { label: 'Message Vendor', onClick: () => onOpenChat(v), testId: 'lifecycle-banner-message' },
      };
      break;
    case 3: // Vendor marked completed — customer must confirm
      banner = {
        tone: 'amber',
        title: `${v.vendor_name} marked the service complete`,
        body: 'Please confirm everything went well.',
        primary: { label: 'Confirm Completion', onClick: () => onConfirmCompletion(v), testId: 'lifecycle-banner-confirm' },
      };
      break;
    case 4: // Final payment due soon
      banner = {
        tone: 'amber',
        title: `Final payment ${countdown ? countdown.toLowerCase() : 'due soon'} for ${v.vendor_name}`,
        body: v.final_amount ? `${fmt(v.final_amount)} remaining` : 'Final payment due soon',
        primary: { label: 'Complete Payment', onClick: () => onPay(v), testId: 'lifecycle-banner-pay' },
      };
      break;
    case 5: // Deposit due soon
      banner = {
        tone: 'amber',
        title: `Deposit ${countdown ? countdown.toLowerCase() : 'due soon'} for ${v.vendor_name}`,
        body: v.deposit_amount ? `${fmt(v.deposit_amount)} to secure this booking` : 'Pay deposit to confirm',
        primary: { label: 'Pay Deposit', onClick: () => onPay(v), testId: 'lifecycle-banner-pay' },
      };
      break;
    case 6: // Proposal received
      banner = {
        tone: 'blue',
        title: `${v.vendor_name} sent you a proposal`,
        body: v.proposal_amount ? `${fmt(v.proposal_amount)} proposed` : 'Review the proposal details.',
        primary: { label: 'Review Proposal', onClick: () => onOpenChat(v), testId: 'lifecycle-banner-review-proposal' },
      };
      break;
    case 7: // Awaiting deposit
      banner = {
        tone: 'amber',
        title: `Pay deposit to lock in ${v.vendor_name}`,
        body: v.deposit_amount ? `${fmt(v.deposit_amount)} to confirm this booking` : 'Pay deposit to confirm',
        primary: { label: 'Pay Deposit', onClick: () => onPay(v), testId: 'lifecycle-banner-pay' },
      };
      break;
    case 8: // Booked — reassurance state
      banner = {
        tone: 'emerald',
        title: `You're booked with ${v.vendor_name}`,
        body: 'We\'ll remind you when final payment is due.',
        primary: { label: 'View Conversation', onClick: () => onOpenChat(v), testId: 'lifecycle-banner-view-chat' },
      };
      break;
    case 9: // Completed, awaiting review
      banner = {
        tone: 'violet',
        title: `Your service with ${v.vendor_name} is complete`,
        body: 'Share your experience with a quick review.',
        primary: { label: 'Leave Review', onClick: () => onLeaveReview(v), testId: 'lifecycle-banner-review' },
      };
      break;
    default:
      return null;
  }

  const tones = {
    red:     { bg: 'bg-red-50',     border: 'border-red-200',     title: 'text-red-800',     body: 'text-red-700',     primary: 'bg-red-600 hover:bg-red-700 text-white',         secondary: 'border-red-200 text-red-700 hover:bg-red-50' },
    amber:   { bg: 'bg-amber-50',   border: 'border-amber-200',   title: 'text-amber-900',   body: 'text-amber-800',   primary: 'bg-amber-600 hover:bg-amber-700 text-white',     secondary: 'border-amber-200 text-amber-700 hover:bg-amber-50' },
    blue:    { bg: 'bg-blue-50',    border: 'border-blue-200',    title: 'text-blue-900',    body: 'text-blue-800',    primary: 'bg-blue-600 hover:bg-blue-700 text-white',       secondary: 'border-blue-200 text-blue-700 hover:bg-blue-50' },
    emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', title: 'text-emerald-900', body: 'text-emerald-800', primary: 'bg-emerald-600 hover:bg-emerald-700 text-white', secondary: 'border-emerald-200 text-emerald-700 hover:bg-emerald-50' },
    violet:  { bg: 'bg-violet-50',  border: 'border-violet-200',  title: 'text-violet-900',  body: 'text-violet-800',  primary: 'bg-violet-600 hover:bg-violet-700 text-white',   secondary: 'border-violet-200 text-violet-700 hover:bg-violet-50' },
  };
  const t = tones[banner.tone] || tones.emerald;

  return (
    <div
      className={`p-4 rounded-xl border ${t.bg} ${t.border}`}
      data-testid="lifecycle-reassurance-banner"
      data-banner-tone={banner.tone}
      data-banner-score={topScore}
    >
      <p className={`text-sm font-semibold ${t.title}`}>{banner.title}</p>
      {banner.body && <p className={`text-xs mt-0.5 ${t.body}`}>{banner.body}</p>}
      <div className="flex flex-wrap gap-2 mt-3">
        <Button
          size="sm"
          className={`text-xs h-8 ${t.primary}`}
          onClick={banner.primary.onClick}
          data-testid={banner.primary.testId}
        >
          {banner.primary.label}
        </Button>
        {banner.secondary && (
          <Button
            size="sm"
            variant="outline"
            className={`text-xs h-8 ${t.secondary}`}
            onClick={banner.secondary.onClick}
            data-testid={banner.secondary.testId}
          >
            {banner.secondary.label}
          </Button>
        )}
      </div>
    </div>
  );
}

function BudgetSection({ budget, vendorTracker = [], selectedVendors = [], defaultExpanded = false }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  const { vendorBreakdown, outstanding, outstandingCount, forecastOnly, forecastOnlyTotal } = useMemo(() => {
    const breakdown = (vendorTracker || [])
      .filter(v => v.booking_id && !BUDGET_CANCELLED_STATUSES.has(v.status))
      .map(v => {
        const depositPaidAmt = v.deposit_paid ? (v.deposit_amount || 0) : 0;
        const finalPaidAmt = v.final_paid ? (v.final_amount || 0) : 0;
        const paid = depositPaidAmt + finalPaidAmt;
        const owed = Math.max((v.agreed_price || 0) - paid, 0);
        return {
          vendor_id: v.vendor_id,
          vendor_name: v.vendor_name,
          category: v.category,
          status: v.status,
          agreed: v.agreed_price || 0,
          paid,
          owed,
        };
      });

    const bookedIds = new Set((vendorTracker || []).filter(v => v.booking_id).map(v => v.vendor_id));
    const fcOnly = (selectedVendors || [])
      .filter(sv => sv.vendor_id && !bookedIds.has(sv.vendor_id))
      .map(sv => ({
        vendor_id: sv.vendor_id,
        business_name: sv.business_name || '(Pending vendor response)',
        category: sv.category,
        starting_price: sv.starting_price || 0,
      }));

    return {
      vendorBreakdown: breakdown,
      outstanding: Math.max((budget.total_agreed || 0) - (budget.total_paid || 0), 0),
      outstandingCount: breakdown.filter(r => r.owed > 0).length,
      forecastOnly: fcOnly,
      forecastOnlyTotal: fcOnly.reduce((s, v) => s + v.starting_price, 0),
    };
  }, [vendorTracker, selectedVendors, budget]);

  if (!budget.budget && !budget.total_agreed) return null;
  return (
    <Card data-testid="budget-summary">
      <CardHeader className="pb-2"><CardTitle className="text-base font-semibold flex items-center gap-2"><DollarSign className="h-4 w-4 text-green-600" />Budget Summary</CardTitle></CardHeader>
      <CardContent>
        {/* Section A — Plan row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
          {budget.budget != null && <div><p className="text-gray-400 text-xs">Budget</p><p className="font-semibold text-gray-900">{fmt(budget.budget)}</p></div>}
          {budget.estimated_total > 0 && <div><p className="text-gray-400 text-xs">Estimated (Forecast)</p><p className="font-semibold text-gray-900">{fmt(budget.estimated_total)}</p></div>}
          {budget.remaining != null && <div><p className="text-gray-400 text-xs">Remaining (Uncommitted)</p><p className={`font-semibold ${budget.remaining >= 0 ? 'text-gray-900' : 'text-red-600'}`}>{fmt(budget.remaining)}</p></div>}
        </div>

        {/* Section B — Summary strip (Agreed / Paid / Outstanding) */}
        {(budget.total_agreed > 0 || budget.total_paid > 0 || outstanding > 0) && (
          <>
            <div className="border-t border-gray-200 my-3" />
            <div className="grid grid-cols-3 gap-4 text-sm" data-testid="budget-summary-strip">
              <div><p className="text-gray-400 text-xs">Agreed</p><p className="font-semibold text-gray-900">{fmt(budget.total_agreed || 0)}</p></div>
              <div><p className="text-gray-400 text-xs">Paid</p><p className="font-semibold text-emerald-700">{fmt(budget.total_paid || 0)}</p></div>
              <div>
                <p className="text-gray-400 text-xs">Outstanding</p>
                <p className={`font-semibold ${outstanding > 0 ? 'text-gray-900' : 'text-gray-400'}`}>{fmt(outstanding)}</p>
              </div>
            </div>
          </>
        )}

        {/* Section C — Committed vendors */}
        {vendorBreakdown.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-medium text-gray-700 uppercase tracking-wide">
                Committed vendors ({vendorBreakdown.length})
              </p>
              <button
                type="button"
                onClick={() => setExpanded(!expanded)}
                className="flex items-center gap-1 text-xs text-gray-500 hover:text-gray-700"
                data-testid="budget-breakdown-toggle"
              >
                {expanded ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
                {expanded ? 'Hide' : 'Show'}
              </button>
            </div>
            {expanded && (
              <div className="space-y-2" data-testid="budget-breakdown-list">
                {vendorBreakdown.map(v => (
                  <div key={v.vendor_id} className="flex items-center justify-between text-sm py-1.5 border-b border-gray-100 last:border-0">
                    <div className="min-w-0 flex-1 pr-2">
                      <p className="font-medium text-gray-900 truncate">{v.vendor_name}</p>
                      <p className="text-xs text-gray-400 truncate">
                        {v.category}
                        {BUDGET_STATUS_LABEL[v.status] && <span className="ml-1.5">· {BUDGET_STATUS_LABEL[v.status]}</span>}
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-semibold text-gray-900 text-sm">{fmt(v.agreed)}</p>
                      <p className="text-xs text-gray-400">
                        {v.paid > 0 && <span className="text-emerald-700">Paid {fmt(v.paid)}</span>}
                        {v.paid > 0 && v.owed > 0 && <span className="mx-1">·</span>}
                        {v.owed > 0 && <span>Owed {fmt(v.owed)}</span>}
                        {v.paid === 0 && v.owed === 0 && <span>—</span>}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Section D — Not yet committed */}
        {forecastOnly.length > 0 && (
          <div className="mt-4 pt-3 border-t border-gray-100" data-testid="budget-forecast-only">
            <p className="text-xs font-medium text-gray-700 uppercase tracking-wide mb-2">
              Not yet committed ({forecastOnly.length})
            </p>
            <div className="space-y-1.5">
              {forecastOnly.map(v => (
                <div key={v.vendor_id} className="flex items-center justify-between text-sm">
                  <div className="min-w-0 flex-1 pr-2">
                    <p className="text-gray-700 truncate">{v.business_name}</p>
                    <p className="text-xs text-gray-400 truncate">{v.category}</p>
                  </div>
                  <p className="text-xs text-gray-500 shrink-0">
                    {v.starting_price > 0 ? `from ${fmt(v.starting_price)}` : '—'}
                  </p>
                </div>
              ))}
              {forecastOnlyTotal > 0 && (
                <div className="flex items-center justify-between text-xs text-gray-500 pt-2 border-t border-gray-50">
                  <span>Forecast total</span>
                  <span className="font-medium text-gray-700">{fmt(forecastOnlyTotal)}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Vendor Action Center (Primary Action Surface) ──
function VendorActionCenter({
  vendorTracker, inquiries, stage, event, getVendorAction, getActionContext,
  getVendorRowAction, handleAddVendors, setConversationInquiryId, navigate,
}) {
  // ── Priority scoring for sort order ──
  const getVendorPriority = (v) => {
    // 1. Overdue payments (highest urgency)
    if (v.lifecycle?.final_payment?.state === 'overdue') return 1;
    if (v.lifecycle?.deposit?.state === 'expired') return 2;
    // 2. Confirm completion
    if (v.status === 'completed_by_vendor') return 3;
    // 3. Requires response / action needed
    if (v.lifecycle?.final_payment?.state === 'warning') return 4;
    if (v.lifecycle?.deposit?.state === 'warning') return 5;
    if (v.status === 'completion_disputed') return 6;
    if (v.status === 'proposal_received') return 7;
    if (v.status === 'responded') return 8;
    if (v.status === 'pending_deposit') return 9;
    if (v.status === 'deposit_paid') return 10;
    // 4. Passive states last
    if (v.status === 'completed') return 20;
    if (v.status === 'fully_paid') return 21;
    if (v.status === 'pending') return 22;
    if (v.status === 'declined') return 30;
    return 25;
  };

  // ── Visual priority tier ──
  const getPriorityTier = (v) => {
    const p = getVendorPriority(v);
    if (p <= 2) return 'urgent';    // red accent
    if (p <= 6) return 'attention'; // amber/softer highlight
    return 'passive';               // minimal styling
  };

  // Sort vendors by priority
  const sortedVendors = [...vendorTracker].sort((a, b) => getVendorPriority(a) - getVendorPriority(b));
  const needsAttentionCount = sortedVendors.filter(v => getPriorityTier(v) !== 'passive').length;

  const isEmpty = vendorTracker.length === 0 && inquiries.length === 0;
  // B-EXP-6.23 — treat closed Concierge (graduated) like completed for
  // the purposes of hiding active-planning vendor CTAs. The stage
  // variable is derived from lifecycle_stage which is 'planning' for a
  // closed case, so we need to also check the raw event.status.
  const isClosed = ['cancelled', 'completed'].includes(stage)
    || event.status === 'closed';

  return (
    <div>
      <div className="flex items-center justify-between mb-3 px-0.5">
        <div>
          <h3 className="text-base font-bold text-gray-900" data-testid="vendor-action-center-heading">Your Vendors</h3>
          {needsAttentionCount > 0 && !isClosed && (
            <p className="text-[11px] font-medium text-amber-600 mt-0.5" data-testid="needs-attention-subheader">
              Needs Attention ({needsAttentionCount})
            </p>
          )}
        </div>
        {(vendorTracker.length > 0 || inquiries.length > 0) && !isClosed && (
          <Button size="sm" fullWidth={false} className="shrink-0 bg-emerald-600 hover:bg-emerald-700 rounded-full text-xs h-8 px-3 sm:px-4" onClick={handleAddVendors} data-testid="add-vendor-inline-btn">
            <Plus className="w-3.5 h-3.5 sm:mr-1" /> <span className="hidden sm:inline">Add Vendor</span><span className="sm:hidden">Add</span>
          </Button>
        )}
      </div>

      {isEmpty ? (
        isClosed ? (
          <div className="bg-white rounded-2xl border border-gray-100/80 p-6 text-center" data-testid="empty-vendor-state">
            <p className="text-sm text-gray-400">No vendors were connected to this event</p>
          </div>
        ) : (
          <div className="bg-white rounded-2xl border border-gray-100/80 p-8" data-testid="empty-vendor-state">
            <div className="text-center mb-6">
              <p className="text-base font-semibold text-gray-900">Start building your {event.event_type?.toLowerCase() || 'event'}</p>
              <p className="text-sm text-gray-400 mt-1.5">Add vendors to bring your event to life</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2">
              <Button onClick={handleAddVendors} className="flex-1 bg-emerald-600 hover:bg-emerald-700 rounded-xl text-sm h-11" data-testid="add-vendor-btn">
                <Plus className="w-4 h-4 mr-1.5" /> Add Vendor
              </Button>
              <Button variant="outline" onClick={() => navigate('/browse')} className="flex-1 rounded-xl text-sm border-gray-200 h-11" data-testid="browse-vendors-btn">
                <Send className="w-4 h-4 mr-1.5" /> Browse Services
              </Button>
            </div>
          </div>
        )
      ) : sortedVendors.length > 0 ? (
        <div className="space-y-2" data-testid="vendor-tracker-list">
          {sortedVendors.map((v) => {
            const s = VENDOR_STATUS[v.status] || VENDOR_STATUS.pending;
            const act = getVendorAction(v);
            const ActIcon = act?.icon;
            const actionCtx = getActionContext(v);
            const completedStep = v.deposit_paid && !v.final_paid ? 'Deposit paid' : v.deposit_paid && v.final_paid ? 'Fully paid' : null;
            const hasAction = !!act;
            const tier = getPriorityTier(v);

            // Visual priority styling
            const rowBorder = tier === 'urgent'
              ? 'border-red-200/80 bg-red-50/20'
              : tier === 'attention'
              ? 'border-amber-200/60 bg-amber-50/10'
              : 'border-gray-100/60 bg-gray-50/30';

            return (
              <div
                key={v.inquiry_id}
                className={`flex items-center justify-between p-3 rounded-xl border hover:bg-white transition-colors cursor-pointer ${rowBorder}`}
                data-testid={`vendor-row-${v.vendor_id}`}
                data-priority-tier={tier}
                onClick={() => getVendorRowAction(v)()}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <VendorAvatar
                    vendor={v}
                    size={40}
                    className="!rounded-xl"
                    testId={`vendor-row-avatar-${v.vendor_id}`}
                  />
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">{v.vendor_name}</p>
                    <p className="text-xs text-gray-400">{v.category}{v.agreed_price ? ` · ${fmt(v.agreed_price)}` : v.proposal_amount ? ` · ${fmt(v.proposal_amount)} proposed` : ''}</p>
                    {actionCtx && <p className={`text-[11px] mt-0.5 ${actionCtx.cls}`}>{actionCtx.text}</p>}
                    {completedStep && hasAction && (
                      <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1"><CheckCircle className="w-2.5 h-2.5" /> {completedStep}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!hasAction && <Badge className={`text-xs border ${s.color}`}>{s.label}</Badge>}
                  {hasAction && (
                    <button
                      onClick={(e) => { e.stopPropagation(); act.action(); }}
                      data-testid={`vendor-action-${v.vendor_id}`}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${act.cls}`}
                    >
                      {ActIcon && <ActIcon className="h-3.5 w-3.5" />}
                      {act.label}
                    </button>
                  )}
                  {/* Remind vendor to respond — only when vendor hasn't replied yet */}
                  {v.status === 'pending' && (
                    <SendReminderButton
                      entityType="inquiry"
                      entityId={v.inquiry_id}
                      nudgeState="proposal_pending"
                      label="Remind to respond"
                      size="icon"
                    />
                  )}
                  {/* Secondary chat access */}
                  {act && !['View Chat', 'Message Vendor', 'View Booking'].includes(act.label) && (
                    <button
                      onClick={(e) => { e.stopPropagation(); setConversationInquiryId(v.inquiry_id); }}
                      className="h-8 w-8 rounded-lg bg-gray-50 hover:bg-gray-100 flex items-center justify-center transition-colors"
                      data-testid={`vendor-chat-${v.vendor_id}`}
                      title="Open conversation"
                    >
                      <MessageSquare className="h-3.5 w-3.5 text-gray-400" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="space-y-2.5">
          {inquiries.map(inquiry => (
            <SimpleVendorCard key={inquiry.id} inquiry={inquiry} onClick={() => setConversationInquiryId(inquiry.id)} />
          ))}
        </div>
      )}
    </div>
  );
}

// ── Simple Vendor Card fallback ──
function SimpleVendorCard({ inquiry, onClick }) {
  const depositPaid = inquiry.deposit_paid;
  const hasProposal = inquiry.booking_status || inquiry.has_proposal;
  const statusColor = depositPaid ? 'emerald' : hasProposal ? 'blue' : inquiry.vendor_accepted ? 'amber' : 'gray';
  const statusLabel = depositPaid ? 'Booked' : hasProposal ? 'Proposal' : inquiry.vendor_accepted ? 'Replied' : inquiry.status === 'pending' ? 'Awaiting' : inquiry.status === 'declined' ? 'Declined' : 'Sent';

  return (
    <button onClick={onClick} className="w-full bg-white rounded-xl border border-gray-100/80 p-4 flex items-center gap-3.5 hover:bg-gray-50/50 transition-colors text-left" data-testid={`vendor-card-${inquiry.id}`}>
      <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center overflow-hidden shrink-0">
        <span className="text-lg font-semibold text-gray-400">{(inquiry.vendor_name || '?')[0]}</span>
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 truncate">{inquiry.vendor_name || 'Vendor'}</p>
        <p className="text-xs text-gray-400 truncate">{inquiry.vendor_category || inquiry.service_type || 'Service Provider'}</p>
      </div>
      <Badge variant="outline" className={`text-[10px] shrink-0 border-${statusColor}-200 text-${statusColor}-700 bg-${statusColor}-50`}>{statusLabel}</Badge>
      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0" />
    </button>
  );
}

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    if (ts.length === 10) return new Date(ts + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' });
  } catch { return ts; }
}

export default EventDetail;
